/**
 * Utility functions for calculating and formatting Volume Load (weight * reps).
 *
 * Volume load is unit-agnostic (works identically for lbs or kg without unit suffix).
 * Safe against non-weight/cardio exercises like treadmill, bodyweight, or missing/invalid inputs.
 */

/**
 * Calculates volume load for a single set.
 * Returns 0 if weight or reps are missing, non-numeric, or <= 0.
 *
 * @param {{ weight?: string|number, reps?: string|number } | null | undefined} set
 * @returns {number}
 */
export function calculateSetVolume(set) {
    if (!set || typeof set !== "object") {
        return 0;
    }

    const weight = typeof set.weight === "number" ? set.weight : parseFloat(set.weight);
    const reps = typeof set.reps === "number" ? set.reps : parseInt(set.reps, 10);

    // Treadmill, bodyweight, empty string "", 0, or negative weights produce 0 volume
    if (!Number.isFinite(weight) || weight <= 0) {
        return 0;
    }

    if (!Number.isFinite(reps) || reps <= 0) {
        return 0;
    }

    return weight * reps;
}

/**
 * Calculates total volume load for an array of sets.
 *
 * @param {Array<{ weight?: string|number, reps?: string|number }> | null | undefined} sets
 * @returns {number}
 */
export function calculateExerciseVolume(sets) {
    if (!Array.isArray(sets) || sets.length === 0) {
        return 0;
    }

    return sets.reduce((sum, set) => sum + calculateSetVolume(set), 0);
}

/**
 * Calculates total volume load for an entire day across all logged exercises.
 * Day log schema: { [exerciseId]: Array<{ weight?: string|number, reps?: string|number }> }
 *
 * @param {Record<string, Array<{ weight?: string|number, reps?: string|number }>> | null | undefined} dayLog
 * @returns {number}
 */
export function calculateDayVolume(dayLog) {
    if (!dayLog || typeof dayLog !== "object") {
        return 0;
    }

    return Object.values(dayLog).reduce((sum, sets) => sum + calculateExerciseVolume(sets), 0);
}

/**
 * Formats a numeric volume load without units.
 * Uses locale-specific number grouping (e.g., "1,250" in en, "1 250" in uk).
 *
 * @param {number} volume
 * @param {string} [locale="en-US"]
 * @param {object} [options]
 * @param {string} [options.emptyPlaceholder="—"] Placeholder when volume is 0 or invalid
 * @returns {string}
 */
export function formatVolumeLoad(volume, locale = "en-US", options = {}) {
    const { emptyPlaceholder = "—" } = options;

    if (!Number.isFinite(volume) || volume <= 0) {
        return emptyPlaceholder;
    }

    const rounded = Math.round(volume * 10) / 10;
    try {
        return new Intl.NumberFormat(locale || "en-US", {
            maximumFractionDigits: 1,
        }).format(rounded);
    } catch (_) {
        return rounded.toLocaleString();
    }
}
